import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import { getFormattedPrice } from "@qgiv/core-donor";
import { useFormikContext } from "formik";
import { determineRegRolesFromSelectedCategoryId } from "@qgiv/p2p-registration";
import { selectSpecificFormSetting } from "../../../redux/slices/formSettingsSlice";
import {
    selectAllP2pSettings,
    selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
} from "../../../redux/slices/p2pSettingsSlice";
import { selectAllCategories } from "../../../redux/slices/categoriesSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import CategoryCard from "./CategoryCard";
import {
    setAppShowRegistrationRole,
    setAppShowRemainingForm,
} from "../../../redux/slices/appSettingsSlice";
import {
    selectCurrentRegistrationIndex,
    selectRegistrationsFromRegistration,
} from "../../../redux/slices/registrationSlice";

const ConnectedCategoryCard = ({
    id,
    multipleCategories,
    categoryRef,
    controlId,
    name,
    type,
}) => {
    const {
        ENUMS: { DefaultView, EntityType },
    } = constants;

    const dispatch = useDispatch();
    const { handleChange, setFieldTouched, setFieldValue } = useFormikContext();
    const currency = useSelector((state) =>
        selectSpecificFormSetting(state, "currency"),
    );
    const p2pSettings = useSelector(selectAllP2pSettings);
    const {
        registrationFeeDisplay,
        registrationFeeDisplayText,
        enableTeams,
        enableIndividualRegistration,
    } = p2pSettings;
    const eventEnabledCommitments = !!p2pSettings?.enableCommitments;
    const isCms = useSelector(selectIsCms);

    const currentRegistration = useSelector(selectCurrentRegistrationIndex);
    const isPrimaryRegistrant = currentRegistration === 0;
    const allowSubRegistrantToChangeRegRole = useSelector(
        selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
    );
    const registrations = useSelector(selectRegistrationsFromRegistration);
    const primaryRegistration = registrations[0];

    const categories = useSelector(selectAllCategories);
    const category = categories?.find((_category) => _category.id === id) || {};
    const {
        description,
        earlyRegistration,
        fee,
        registrationFee,
        title,
        enableCommitments,
        commitmentAmount,
    } = category;

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_DETAILS_STEP,
            state,
        ),
    );

    const { options = {} } = control;
    const _category = options?.category || {};
    const {
        commitmentLabel = "Commit to raising at least %CommitmentAmount% by %CommitmentDeadline% or cover the difference.",
    } = _category;

    // Infer props from settings
    const isFree = registrationFee === 0;

    const formattedPrice = getFormattedPrice(
        registrationFee,
        currency,
        registrationFeeDisplay,
        registrationFeeDisplayText,
    );
    const formattedDiscountedPrice = currencyString(fee, currency);
    const hasDiscount = isFree ? false : earlyRegistration;

    const hasCommitments =
        eventEnabledCommitments &&
        enableCommitments &&
        commitmentAmount &&
        commitmentAmount > 0;

    const handleChangeWhenOnlyRoleAvailableIsIndividual = () => {
        // Since there is only one choice for Registration Role, we skip loading the RoleCards.
        // This means we need to manually set the Registration Role for Individual and then display
        // the rest of the form.
        setFieldTouched("Registration_Role");
        setFieldValue("Registration_Role", [`${EntityType.REGISTRATION}`]);
        dispatch(setAppShowRemainingForm(true));
    };

    const handleChangeWhenOnlyRoleAvailableIsTeam = () => {
        // Since there is only one choice for Registration Role, we skip loading the RoleCards.
        // This means we need to manually set the Registration Role for Team and then display
        // the team Role selected.
        setFieldTouched("Registration_Role");
        setFieldValue("Registration_Role", [`${EntityType.TEAM}`]);
        dispatch(setAppShowRegistrationRole(true));

        // Preselect Team and show form when Additional Participant and
        // allowSubRegistrantToChangeRegRole is disabled
        if (!isPrimaryRegistrant && !allowSubRegistrantToChangeRegRole) {
            dispatch(setAppShowRemainingForm(true));
            setFieldValue("Team", primaryRegistration.Team);
            setFieldValue("Team_Captain", false);
        }
        if (isPrimaryRegistrant && primaryRegistration.Team !== "new-team") {
            setFieldValue("Team_Captain", false);
        }
    };

    const handleChangeWhenBothRolesAvailable = () => {
        const hasPreselectedRole =
            registrations[currentRegistration]?.Registration_Role?.length > 0;

        if (hasPreselectedRole) {
            setFieldTouched("Registration_Role");
            setFieldValue(
                "Registration_Role",
                registrations[currentRegistration].Registration_Role,
            );
            dispatch(setAppShowRegistrationRole(true));
            dispatch(setAppShowRemainingForm(true));
        } else {
            setFieldTouched("Registration_Role", false);
            setFieldValue("Registration_Role", []);
            dispatch(setAppShowRegistrationRole(true));
        }

        // Preselect Team and show form when Additional Participant and
        // allowSubRegistrantToChangeRegRole is disabled
        if (!isPrimaryRegistrant && !allowSubRegistrantToChangeRegRole) {
            dispatch(setAppShowRemainingForm(true));
            setFieldTouched("Registration_Role");
            setFieldValue("Registration_Role", [
                `${primaryRegistration.Registration_Role}`,
            ]);
            if (
                Number(primaryRegistration.Registration_Role) ===
                EntityType.TEAM
            ) {
                setFieldValue("Team", primaryRegistration.Team);
                setFieldValue("Team_Captain", false);
            }
        }
        if (isPrimaryRegistrant && primaryRegistration.Team !== "new-team") {
            setFieldValue("Team_Captain", false);
        }
    };

    const handleCategoryChange = (e) => {
        // Get Category id off e.target.value
        const selectedCategoryId = e.target.value;
        const isCategorySelected = e.target.checked;
        // Formik function for select Category
        handleChange(e);

        if (isCategorySelected && selectedCategoryId) {
            // Determine roles available to this category
            const {
                isRegRoleIndividualOnly,
                isRegRoleTeamOnly,
                isRegRoleTeamAndIndividual,
            } = determineRegRolesFromSelectedCategoryId({
                selectedCategoryId,
                categories,
                enableTeams,
                enableIndividualRegistration,
            });

            // Call appropriate handleChange function based on Category Role Availability
            if (isRegRoleIndividualOnly) {
                handleChangeWhenOnlyRoleAvailableIsIndividual();
            }

            if (isRegRoleTeamOnly) {
                handleChangeWhenOnlyRoleAvailableIsTeam();
            }
            if (isRegRoleTeamAndIndividual) {
                handleChangeWhenBothRolesAvailable();
            }
        } else {
            dispatch(setAppShowRemainingForm(false));
        }
    };

    const categoryCardProps = {
        categoryRef,
        commitmentAmount,
        description,
        details: commitmentLabel,
        earlyRegistration,
        hasCommitments,
        formattedDiscountedPrice,
        formattedPrice,
        hasDiscount,
        id,
        isCms,
        isFree,
        multipleCategories,
        title,
        name,
        type,
        handleCategoryChange,
    };

    return <CategoryCard {...categoryCardProps} />;
};

ConnectedCategoryCard.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    categoryRef: PropTypes.shape({}).isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    multipleCategories: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
};

export default ConnectedCategoryCard;
